import {
  Button,
  ButtonGroup,
  Flex,
  Grid,
  Input,
  Label,
  View,
} from "@aws-amplify/ui-react"
import { OrgSettingsUpdateSchema } from "@strtmn/sds/schemas"
import type { OrganizationDto, OrgSettingsUpdateDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { icon } from "leaflet"
import { useState, useEffect } from "react"
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form"
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet"
import { useNavigate } from "react-router-dom"
import { createOrg, updateOrgSettings } from "../../api/OrgsApi"
import { isDev } from "../../config"
import { CancelButton, CopyToClipboard } from ".."

const BslMarkerIcon = icon({
  iconUrl: "/marker-icon.png",
  iconRetinaUrl: "/marker-icon-2x.png",
  shadowUrl: "/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41],
})

interface OrgMaskProps {
  org: OrganizationDto
  isNewOrg: boolean
}

export default function OrgMask({ org, isNewOrg }: OrgMaskProps) {
  console.log("> OrgMask")
  console.log("org:", org)
  console.log("isNewOrg:", isNewOrg)
  const navigate = useNavigate()
  const [isFormSubmitting /*, setIsFormSubmitting*/] = useState(false)

  const methods = useForm<OrganizationDto>({
    values: org,
  })
  const {
    register,
    handleSubmit,
    setError,
    // setValue,
    formState: { errors },
    watch,
  } = methods

  // A default is needed, otherwise the map will break. Use BSL as default.
  const BslPosition = { lat: 52.102019, lng: 8.101101 }
  const DefaultPosition = BslPosition
  const position =
    watch("address.gps.lat") && watch("address.gps.lng")
      ? {
          lat: watch("address.gps.lat")!,
          lng: watch("address.gps.lng")!,
        }
      : DefaultPosition

  const address =
    org.address && typeof org.address === "object" ? org.address : undefined

  function MapUpdater({
    position,
  }: {
    position?: { lat: number; lng: number }
  }) {
    const map = useMap()
    useEffect(() => {
      if (position) {
        if (
          Math.abs(position.lat - DefaultPosition.lat) > 0.0001 ||
          Math.abs(position.lng - DefaultPosition.lng) > 0.0001
        ) {
          map.setZoom(13)
        }
        map.setView([position.lat, position.lng])
      }
    }, [position, map])
    return null
  }

  const onSubmit: SubmitHandler<OrganizationDto> = async (data) => {
    console.log("> onSubmit")
    isDev && console.log("data:", data)
    try {
      const isAddressEmptyString = data.address === ""
      // Ensure an empty address validates
      if (isAddressEmptyString) {
        data.address = {
          gps: null,
        }
      }
      const orgSettings: OrgSettingsUpdateDto =
        await OrgSettingsUpdateSchema.validate(data, { stripUnknown: true })
      // Revert validation patch
      if (isAddressEmptyString) {
        orgSettings.address = ""
      }
      isDev && console.log("orgSettings", orgSettings)
      if (isNewOrg) {
        await createOrg(orgSettings)
      } else {
        await updateOrgSettings(data.id, orgSettings)
      }
      isDev && console.log("onSubmit: success")
      navigate("..")
    } catch (err) {
      isDev && console.log("onSubmit: caught", err)
      setError("root.serverError", {
        message:
          `${I18n.get("Fehler")}: ${I18n.get("Konnte den Betrieb nicht speichern.")}` +
          (isDev ? " " + JSON.stringify(err, null, 2) : ""),
      })
    }
  }

  return (
    <Grid rowGap="0.5rem" columnGap="0.5rem" templateColumns="1fr 1fr">
      <View column={1}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors?.root && errors?.root["clientError"] && (
              <p className="error">{errors.root["clientError"].message}</p>
            )}
            {errors?.root && errors?.root["serverError"] && (
              <p className="error">{errors.root["serverError"].message}</p>
            )}

            <Label>{I18n.get("Name")}</Label>
            <Input
              placeholder={I18n.get("Name")}
              defaultValue={org.name}
              {...register("name", {
                required: I18n.get("Geben Sie einen Namen an"),
              })}
              hasError={!!errors.name}
            />

            <Label>{I18n.get("Straße und Hausnummer")}</Label>
            <Flex>
              <Input
                width="85%"
                placeholder={I18n.get("Straße")}
                defaultValue={address?.street}
                {...register("address.street")}
              />
              <Input
                width="15%"
                placeholder={I18n.get("Hausnummer")}
                defaultValue={address?.number}
                {...register("address.number")}
              />
            </Flex>

            <Label>{I18n.get("Land, Postleitzahl und Ort")}</Label>
            <Flex>
              <Input
                width="13%"
                placeholder={I18n.get("Land")}
                defaultValue={address?.country}
                {...register("address.country", {
                  maxLength: {
                    value: 2,
                    message: I18n.get(
                      "Geben Sie das Land als Code mit 2 Buchstaben an, z.B. DE."
                    ),
                  },
                })}
              />
              <Input
                width="20%"
                placeholder={I18n.get("Postleitzahl")}
                defaultValue={address?.zip}
                {...register("address.zip")}
              />
              <Input
                width="67%"
                placeholder={I18n.get("Ort")}
                defaultValue={address?.city}
                {...register("address.city")}
              />
            </Flex>

            <Label>{I18n.get("GPS-Koordinaten")}</Label>
            <Input
              placeholder={I18n.get("Längengrad")}
              defaultValue={address?.gps?.lat}
              type="number"
              step="any"
              {...register("address.gps.lat")}
            />
            <Input
              placeholder={I18n.get("Breitengrad")}
              defaultValue={address?.gps?.lng}
              type="number"
              step="any"
              {...register("address.gps.lng")}
            />

            <small>
              Hier kann man eine Adresse in Längen- und Breitengrad
              konvertieren:
              <br />
              <a
                href="https://www.latlong.net/convert-address-to-lat-long.html"
                target="_blank"
                rel="noreferrer"
              >
                https://www.latlong.net/convert-address-to-lat-long.html
              </a>
              <br />
              Adresse kopieren: {watch("address.street")}{" "}
              {watch("address.number")}, {watch("address.country")}{" "}
              {watch("address.zip")} {watch("address.city")}{" "}
              <CopyToClipboard
                text={`${watch("address.street")} ${watch("address.number")}, ${watch("address.country")} ${watch("address.zip")} ${watch("address.city")}`}
              />
            </small>

            {/* <Label>{I18n.get("Typ")}</Label>
            <SelectField
              label={I18n.get("Typ")}
              labelHidden={true}
              defaultValue={group.type}
              {...register("type")}
            >
              <option key="MANUFACTURER" value="MANUFACTURER">
                Manufacturer
              </option>
              <option key="DEALER" value="DEALER">
                Dealer
              </option>
              <option key="CUSTOMER" value="CUSTOMER">
                Customer
              </option>
            </SelectField> */}

            <div>{/* divider */}&nbsp;</div>

            {/* <Label>{I18n.get("Untergruppe von")}</Label>
            <SelectField
              label={I18n.get("Untergruppe von")}
              labelHidden={true}
              defaultValue={group.parentId}
              {...register("parentId")}
            >
              {allGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </SelectField> */}

            <div>{/* divider */}&nbsp;</div>

            <Label>{I18n.get("Benutzer")}</Label>
            {!org.Users ||
            !Array.isArray(org.Users) ||
            org.Users.length === 0 ? (
              <div>
                Diese Liste ist kaputt. Man muss bei den Benutzern nachsehen.
                {/* {I18n.get("Diesem Betrieb sind keine Benutzer zugewiesen.")} */}
              </div>
            ) : (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>{I18n.get("User")}</th>
                    <th>{I18n.get("Rolle")}</th>
                  </tr>
                </thead>
                <tbody>
                  {org.Users &&
                    org.Users.map((orgUser) => (
                      <tr key={orgUser.id}>
                        <td>
                          {orgUser.firstName} {orgUser.lastName}
                        </td>
                        <td>
                          {orgUser.user_role ??
                            I18n.get("Keine Rolle festgelegt")}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            <div>{/* divider */}&nbsp;</div>

            <Label>{I18n.get("Maschinen")}</Label>
            {!org.Machines ||
            !Array.isArray(org.Machines) ||
            org.Machines.length === 0 ? (
              <div>
                Diese Liste ist kaputt. Man muss bei den Maschinen nachsehen.
                {/* {I18n.get("Diesem Betrieb sind keine Maschinen zugewiesen.")} */}
              </div>
            ) : (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>{I18n.get("Maschine")}</th>
                    <th>{I18n.get("Modell")}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* {group.Machines.map((machine) => (
                    <tr key={machine.id}>
                      <td>{machine.serialNumber}</td>
                      <td>{I18n.get(machine.model)}</td>
                    </tr>
                  ))} */}
                </tbody>
              </table>
            )}

            <ButtonGroup className="buttons !mt-4" size="small">
              <Button
                type="submit"
                variation="primary"
                isDisabled={isFormSubmitting}
                isLoading={isFormSubmitting}
                loadingText={I18n.get("Wird gespeichert...")}
              >
                {I18n.get("Speichern")}
              </Button>
              <CancelButton
                href=".."
                title="Abbrechen"
                isDisabled={isFormSubmitting}
              />
            </ButtonGroup>
          </form>
        </FormProvider>
      </View>

      <View column={2} style={{ width: "100%", height: "28rem" }}>
        <MapContainer
          style={{ width: "100%", height: "28rem" }}
          center={position}
          zoom={12}
          scrollWheelZoom={true}
          className="w-full h-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapUpdater position={position} />
          <Marker icon={BslMarkerIcon} position={position!}>
            <Popup>{I18n.get("Standort")}</Popup>
          </Marker>
        </MapContainer>

        <small>
          Org-ID: {org.id} <CopyToClipboard text={org.id} />
        </small>
      </View>
    </Grid>
  )
}
